import { Button, Input, useToast } from '@zyxantech/tux'
import { AlertCircle, KeyRound, Lock, Mail } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useResetPasswordMutation } from '@/api/auth.api'
import { ROUTES } from '@/constants/routes'

interface ResetState {
  email?: string
}

export function ResetPasswordPage() {
  const [resetPassword, { isLoading }] = useResetPasswordMutation()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()

  const prefillEmail = (location.state as ResetState | null)?.email ?? ''
  const [email, setEmail] = useState(prefillEmail)
  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !otp.trim() || !newPassword) {
      setError('All fields are required.')
      return
    }
    setError(null)
    try {
      await resetPassword({ email, otp, newPassword }).unwrap()
      toast.show('Password reset. Please sign in with your new password.', 'success')
      navigate(ROUTES.LOGIN, { replace: true })
    } catch {
      setError('Invalid or expired reset code. Please request a new one.')
    }
  }

  return (
    <div className="w-full">
      <div className="mb-7">
        <h1 className="text-xl font-semibold text-text-primary">Set a new password</h1>
        <p className="mt-1 text-[13px] text-text-muted">
          Enter the reset code from your email and choose a new password.
        </p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="username"
          leadingIcon={<Mail size={15} />}
        />
        <Input
          label="Reset code"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="6-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          leadingIcon={<KeyRound size={15} />}
        />
        <Input
          label="New password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="••••••••"
          autoComplete="new-password"
          leadingIcon={<Lock size={15} />}
        />

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-danger bg-danger/10 px-3 py-2.5 text-xs leading-relaxed text-danger">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button type="submit" variant="primary" loading={isLoading} className="mt-1 w-full">
          Reset password
        </Button>
      </form>

      <div className="mt-6 text-center text-xs text-text-muted">
        <Link to={ROUTES.LOGIN} className="text-accent hover:underline">
          Back to sign in
        </Link>
      </div>
    </div>
  )
}
